import { BrokerCommunication } from "@dune/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Button,
	Checkbox,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@dune/ui";
import { formatRelativeDateTime } from "@dune/utils";
import { UseMutationResult } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosResponse } from "axios";
import {
	Archive,
	ArrowDownLeft,
	ArrowUpRight,
	Forward,
	Paperclip,
	Trash2,
} from "lucide-react";
import { memo, useCallback, useState } from "react";

interface CommunicationsColumnsProps {
	onUpdateProcessed: UseMutationResult<
		AxiosResponse<unknown>,
		Error,
		{ emailId: number; isProcessed: boolean },
		unknown
	>;
	onArchive: UseMutationResult<
		AxiosResponse<unknown>,
		Error,
		{ emailId: number },
		unknown
	>;
	onDeleteDraft: UseMutationResult<
		AxiosResponse<unknown>,
		Error,
		number,
		unknown
	>;
}

const getDirectionIconColor = (direction: string, isForwarded: boolean) => {
	if (isForwarded) return "text-indigo-500";
	if (direction === "INBOUND") return "text-emerald-500";
	return "text-amber-500";
};

const ProcessedCheckbox = memo(
	({
		mail,
		onUpdateProcessed,
	}: {
		mail: BrokerCommunication;
		onUpdateProcessed: UseMutationResult<
			AxiosResponse<unknown>,
			Error,
			{ emailId: number; isProcessed: boolean },
			unknown
		>;
	}) => {
		const [optimisticProcessed, setOptimisticProcessed] = useState(
			mail.isProcessed,
		);

		const handleCheckedChange = useCallback(
			(checked: boolean) => {
				setOptimisticProcessed(checked);

				onUpdateProcessed.mutate(
					{ emailId: mail.id, isProcessed: checked },
					{
						onError: () => {
							setOptimisticProcessed(mail.isProcessed);
						},
					},
				);
			},
			[mail.id, mail.isProcessed, onUpdateProcessed],
		);

		return (
			<Checkbox
				checked={optimisticProcessed}
				onClick={(e) => e.stopPropagation()}
				onCheckedChange={handleCheckedChange}
				label=""
			/>
		);
	},
);

export const communicationsColumns = ({
	onUpdateProcessed,
	onArchive,
	onDeleteDraft,
}: CommunicationsColumnsProps): ColumnDef<BrokerCommunication>[] => [
	{
		accessorKey: "subject",
		header: "Sujet",
		cell: ({ row }) => {
			const mail = row.original;
			const isForwarded = /^[Ff]wd:/i.test(mail.subject || "");
			const iconColor = getDirectionIconColor(
				mail.direction || "OUTBOUND",
				isForwarded,
			);

			return (
				<div className="flex items-center overflow-hidden w-full min-w-0">
					{!mail.isProcessed && (
						<span className="w-2 h-2 bg-blue-500 rounded-full mr-2 flex-shrink-0" />
					)}
					{isForwarded ? (
						<Forward
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Transféré"
						/>
					) : mail.direction === "INBOUND" ? (
						<ArrowDownLeft
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Email reçu"
						/>
					) : (
						<ArrowUpRight
							size={16}
							className={`${iconColor} mr-1 flex-shrink-0`}
							aria-label="Email envoyé"
						/>
					)}
					<span className="truncate font-medium flex-1 min-w-0">
						{mail.subject || "Sans objet"}
					</span>
					{mail.attachments && mail.attachments.length > 0 && (
						<Paperclip size={16} className="ml-1 text-gray-500 flex-shrink-0" />
					)}
					{mail.status === "DRAFT" && (
						<>
							<span className="ml-2 px-2 py-0.5 rounded border border-yellow-400 bg-yellow-100 text-yellow-800 text-xs font-semibold flex-shrink-0">
								Brouillon
							</span>
							<Button
								variant="ghost"
								size="icon"
								onClick={(e) => {
									e.stopPropagation();
									onDeleteDraft.mutate(mail.id);
								}}
								aria-label="Supprimer le brouillon"
								className="size-5 rounded-sm ml-1 flex-shrink-0"
							>
								<Trash2 size={16} className="text-red-500" />
							</Button>
						</>
					)}
				</div>
			);
		},
	},
	{
		accessorKey: "from",
		header: "De",
		cell: ({ row }) => (
			<div className="truncate w-full min-w-0 max-w-[220px]">
				{row.original.from || "Expéditeur inconnu"}
			</div>
		),
	},
	{
		accessorKey: "to",
		header: "À",
		cell: ({ row }) => (
			<div className="truncate w-full min-w-0 max-w-[220px]">
				{row.original.to?.length > 0
					? row.original.to.join(", ")
					: "Destinataire inconnu"}
			</div>
		),
	},
	{
		accessorKey: "sentAt",
		header: "Envoyé le",
		cell: ({ row }) => (
			<div className="max-w-[100px] min-w-[80px]">
				<span className="whitespace-nowrap text-sm">
					{formatRelativeDateTime(row.original.date)}
				</span>
			</div>
		),
	},
	{
		accessorKey: "actions",
		header: "Actions",
		enableSorting: false,
		cell: ({ row }) => {
			const mail = row.original;
			return (
				<div className="flex items-center justify-center space-x-1 min-w-0 max-w-[44px]">
					<ProcessedCheckbox
						mail={mail}
						onUpdateProcessed={onUpdateProcessed}
					/>
					<AlertDialog>
						<Tooltip>
							<TooltipTrigger asChild>
								<AlertDialogTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										onClick={(e) => e.stopPropagation()}
										className={`size-5 rounded-sm ${
											onArchive.status === "pending"
												? "pointer-events-none cursor-not-allowed opacity-50"
												: ""
										}`}
										aria-label="Archiver le mail"
									>
										<Archive size={16} />
									</Button>
								</AlertDialogTrigger>
							</TooltipTrigger>
							<TooltipContent sideOffset={8}>Archiver le mail</TooltipContent>
						</Tooltip>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Confirmer l'archivage</AlertDialogTitle>
								<AlertDialogDescription>
									Êtes-vous sûr de vouloir archiver ce mail ?
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel>Annuler</AlertDialogCancel>
								<AlertDialogAction asChild>
									<Button
										variant="destructive"
										onClick={(e) => {
											e.preventDefault();
											onArchive.mutate({ emailId: mail.id });
										}}
										disabled={onArchive.status === "pending"}
										isLoading={onArchive.status === "pending"}
									>
										Archiver
									</Button>
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</div>
			);
		},
	},
];
